import { useState } from "react";
import "../content.css";

// ─── STUDY PLANNER ALGORITHM ──────────────────────────────────────────────────
//
// Fixes from previous version:
//  1. Buffer days don't consume study capacity — actualStudyDays excludes them,
//     so the feasibility check and urgency math are always accurate.
//  2. Urgency = remaining / realStudyDaysLeft (days left AFTER today, buffers
//     excluded) — gives each subject a real hrs/day pressure score.
//  3. Proportional share is recomputed each inner iteration from remaining
//     subjects not yet allocated that loop pass — no early-subject bloat.
//  4. 60% cap is removed when only 1 active subject remains — no wasted hours.
//  5. Leftover hours after the main loop are redistributed proportionally
//     across ALL unfinished subjects, not just the biggest.
//  6. Subjects that finish early unlock "early_revision" days instead of
//     silently wasting their time slots.
//
// Difficulty weight (extra spread needed): Easy 1.0 | Medium 1.3 | Hard 1.6
// ─────────────────────────────────────────────────────────────────────────────

const DIFF_COLOR = {
  Easy:   { bg: "#1a2e1a", text: "#4caf6e", border: "#2d4a2d" },
  Medium: { bg: "#2e2a1a", text: "#c8a96e", border: "#4a3d1a" },
  Hard:   { bg: "#2e1a1a", text: "#e07070", border: "#4a2d2d" },
};
const DIFF_WEIGHT = { Easy: 1.0, Medium: 1.3, Hard: 1.6 };

function fmtDate(base, days) {
  const d = new Date(base);
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function generatePlan(name, examDate, subjects, freeHoursPerDay) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const exam  = new Date(examDate); exam.setHours(0, 0, 0, 0);
  const totalDays = Math.ceil((exam - today) / 86400000);

  if (totalDays < 0) return { error: "Exam date is in the past." };
  if (totalDays === 0) return { error: "exam_today" };

  const freeHours    = Number(freeHoursPerDay);
  const REVISION_DAYS = totalDays <= 3 ? 1 : 2;
  const calStudyDays  = totalDays - REVISION_DAYS; // calendar days before revision

  // Pre-mark buffer days (every 5th calendar study day, only if plan is long enough)
  const bufferSet = new Set();
  if (calStudyDays > 6) {
    for (let d = 5; d <= calStudyDays; d += 5) bufferSet.add(d);
  }

  // FIX 1: actual productive days excludes buffers
  const actualStudyDays = calStudyDays - bufferSet.size;

  // Build weighted pool
  const pool = subjects.map(s => {
    const w      = DIFF_WEIGHT[s.difficulty] || 1.0;
    const needed = Math.round(Number(s.hours) * w * 100) / 100;
    return {
      name: s.subject, difficulty: s.difficulty,
      originalHours: Number(s.hours),
      remaining: needed, totalWeighted: needed,
      color: DIFF_COLOR[s.difficulty],
    };
  });

  const totalWeightedHours  = Math.round(pool.reduce((a, p) => a + p.totalWeighted, 0) * 100) / 100;
  // FIX 5: feasibility uses actualStudyDays
  const totalStudyCapacity  = Math.round(actualStudyDays * freeHours * 100) / 100;
  const feasible            = totalStudyCapacity >= totalWeightedHours;

  const calendar = [];
  let studyDaysSoFar = 0;

  for (let calDay = 1; calDay <= calStudyDays; calDay++) {
    const dateLabel = fmtDate(today, calDay - 1);

    if (bufferSet.has(calDay)) {
      calendar.push({ day: calDay, type: "buffer", date: dateLabel });
      continue;
    }

    studyDaysSoFar++;
    // FIX 2: real study days remaining including today
    const realStudyDaysLeft = actualStudyDays - studyDaysSoFar + 1;

    const active = pool.filter(s => s.remaining > 0.005);

    if (active.length === 0) {
      calendar.push({ day: calDay, type: "early_revision", date: dateLabel, hours: freeHours });
      continue;
    }

    // FIX 4: relax cap when only 1 subject left
    const maxPerSubject = active.length === 1 ? freeHours : Math.round(freeHours * 0.60 * 100) / 100;

    // FIX 2: sort by true urgency = hrs/day needed to finish in time
    const sorted = active
      .map(s => ({ sub: s, urgency: s.remaining / realStudyDaysLeft }))
      .sort((a, b) => b.urgency - a.urgency);

    let hoursLeft = freeHours;
    const sessions = [];

    for (let i = 0; i < sorted.length; i++) {
      if (hoursLeft <= 0.005) break;
      const { sub } = sorted[i];

      // FIX 3: proportional share from remaining unallocated subjects this pass
      const poolLeft = sorted.slice(i).reduce((a, x) => a + x.sub.remaining, 0);
      const proportional = poolLeft > 0 ? (sub.remaining / poolLeft) * hoursLeft : hoursLeft;

      let alloc = Math.min(proportional, sub.remaining, maxPerSubject, hoursLeft);
      alloc = Math.round(alloc * 100) / 100;
      if (alloc <= 0) continue;

      sessions.push({ subject: sub.name, hours: alloc, difficulty: sub.difficulty, color: sub.color });
      sub.remaining = Math.max(0, Math.round((sub.remaining - alloc) * 100) / 100);
      hoursLeft     = Math.max(0, Math.round((hoursLeft     - alloc) * 100) / 100);
    }

    // FIX 5: redistribute leftover proportionally across ALL unfinished subjects
    if (hoursLeft > 0.01) {
      const unfinished = pool.filter(s => s.remaining > 0.005);
      const totalUnfinished = unfinished.reduce((a, s) => a + s.remaining, 0);
      for (const sub of unfinished) {
        if (hoursLeft <= 0.005) break;
        const share  = totalUnfinished > 0 ? (sub.remaining / totalUnfinished) * hoursLeft : 0;
        const actual = Math.round(Math.min(share, hoursLeft) * 100) / 100;
        if (actual <= 0) continue;
        const existing = sessions.find(s => s.subject === sub.name);
        if (existing) existing.hours = Math.round((existing.hours + actual) * 100) / 100;
        else sessions.push({ subject: sub.name, hours: actual, difficulty: sub.difficulty, color: sub.color });
        sub.remaining = Math.max(0, Math.round((sub.remaining - actual) * 100) / 100);
        hoursLeft     = Math.max(0, Math.round((hoursLeft     - actual) * 100) / 100);
      }
    }

    const totalStudied = Math.round(sessions.reduce((a, s) => a + s.hours, 0) * 100) / 100;
    calendar.push({ day: calDay, type: "study", date: dateLabel, sessions, totalStudied });
  }

  // Revision days
  for (let r = 1; r <= REVISION_DAYS; r++) {
    const dayNum = calStudyDays + r;
    calendar.push({
      day: dayNum, type: "revision",
      date: fmtDate(today, dayNum - 1),
      subjects: subjects.map(s => s.subject),
      hours: freeHours,
    });
  }

  // Coverage stats
  const stats = subjects.map(s => {
    const p       = pool.find(p => p.name === s.subject);
    const covered = p ? Math.max(0, p.totalWeighted - p.remaining) : 0;
    const pct     = p ? Math.min(100, Math.round((covered / p.totalWeighted) * 100)) : 0;
    const scheduled = Math.round(covered / (DIFF_WEIGHT[s.difficulty] || 1) * 100) / 100;
    return { name: s.subject, pct, difficulty: s.difficulty, color: p?.color, scheduled, originalHours: Number(s.hours) };
  });

  return {
    name, totalDays, studyDays: actualStudyDays,
    revisionDays: REVISION_DAYS, bufferCount: bufferSet.size,
    calendar, stats, feasible,
    totalWeightedHours, totalStudyCapacity,
  };
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────

export default function Content() {
  const [name,       setName]       = useState("");
  const [examDate,   setExamDate]   = useState("");
  const [freeHours,  setFreeHours]  = useState("");
  const [subjects,   setSubjects]   = useState([{ subject: "", hours: "", difficulty: "Medium" }]);
  const [plan,       setPlan]       = useState(null);
  const [error,      setError]      = useState("");
  const [expanded,   setExpanded]   = useState({});

  const addSubject    = () => setSubjects([...subjects, { subject: "", hours: "", difficulty: "Medium" }]);
  const removeSubject = (i) => { if (subjects.length > 1) setSubjects(subjects.filter((_, idx) => idx !== i)); };
  const updateSubject = (i, field, value) => { const u = [...subjects]; u[i][field] = value; setSubjects(u); };
  const toggle        = (day) => setExpanded(p => ({ ...p, [day]: !p[day] }));

  const handleGenerate = () => {
    setError(""); setPlan(null);
    if (!name.trim())                                                        { setError("Please enter your name."); return; }
    if (!examDate)                                                           { setError("Please select your exam date."); return; }
    if (subjects.some(s => !s.subject.trim() || !s.hours || Number(s.hours) <= 0)) { setError("Fill in all subjects with valid hours."); return; }
    if (!freeHours || Number(freeHours) <= 0)                               { setError("Please enter valid daily study hours."); return; }

    const result = generatePlan(name, examDate, subjects, freeHours);
    if (result.error === "exam_today") { setError("Your exam is TODAY! Good luck! 🍀"); return; }
    if (result.error)                  { setError(result.error); return; }

    setPlan(result);
    const init = {};
    result.calendar.slice(0, 3).forEach(d => { init[d.day] = true; });
    setExpanded(init);
  };

  return (
    <main className="content" id="planner">

      {/* Hero */}
      <section className="hero">
        <div className="hero-eyebrow">Smart Study Planning</div>
        <h2 className="hero-title">Your exam.<br /><span className="hero-accent">Your roadmap.</span></h2>
        <p className="hero-desc">
          Enter your subjects, difficulty level, and daily availability — we'll
          build a precision schedule with urgency-based allocation, rest days, and
          guaranteed revision time.
        </p>
      </section>

      {/* Form */}
      <section className="form-section">
        <div className="form-card">

          <div className="form-row two-col">
            <div className="field">
              <label className="field-label">Your Name</label>
              <input className="input" type="text" placeholder="e.g. Alex"
                value={name} onChange={e => setName(e.target.value)} />
            </div>
            <div className="field">
              <label className="field-label">Exam Date</label>
              <input className="input" type="date"
                value={examDate} onChange={e => setExamDate(e.target.value)} />
            </div>
          </div>

          <div className="field">
            <label className="field-label">Free Study Hours Per Day</label>
            <input className="input half" type="number" min="0.5" max="16" step="0.5"
              placeholder="e.g. 5" value={freeHours} onChange={e => setFreeHours(e.target.value)} />
          </div>

          <div className="field">
            <label className="field-label">Subjects & Difficulty</label>
            <div className="subjects-list">
              {subjects.map((sub, i) => (
                <div className="subject-row" key={i}>
                  <div className="subject-index">{i + 1}</div>
                  <input className="input sub-name" type="text" placeholder="Subject name"
                    value={sub.subject} onChange={e => updateSubject(i, "subject", e.target.value)} />
                  <input className="input sub-hours" type="number" placeholder="Hours needed" min="0.5" step="0.5"
                    value={sub.hours} onChange={e => updateSubject(i, "hours", e.target.value)} />
                  <select className="input sub-diff"
                    value={sub.difficulty} onChange={e => updateSubject(i, "difficulty", e.target.value)}>
                    <option>Easy</option>
                    <option>Medium</option>
                    <option>Hard</option>
                  </select>
                  <button className="remove-btn" onClick={() => removeSubject(i)}
                    disabled={subjects.length === 1} title="Remove">×</button>
                </div>
              ))}
            </div>
            <button className="add-btn" onClick={addSubject}>+ Add subject</button>
          </div>

          {error && <div className="error-banner">⚠ {error}</div>}

          <button className="generate-btn" onClick={handleGenerate}>
            Build My Study Plan →
          </button>
        </div>
      </section>

      {/* Output */}
      {plan && (
        <section className="plan-section">

          <div className="plan-summary">
            {[
              { num: plan.totalDays,          label: "Days to exam" },
              { num: plan.studyDays,           label: "Study days" },
              { num: plan.revisionDays,        label: "Revision days" },
              { num: plan.bufferCount,         label: "Rest days" },
              { num: `${plan.totalStudyCapacity}h`, label: "Total capacity" },
            ].map((item, i, arr) => (
              <>
                <div className="summary-item" key={i}>
                  <span className="summary-num">{item.num}</span>
                  <span className="summary-label">{item.label}</span>
                </div>
                {i < arr.length - 1 && <div className="summary-divider" key={`d${i}`} />}
              </>
            ))}
          </div>

          {!plan.feasible && (
            <div className="warn-banner">
              ⚡ Available capacity ({plan.totalStudyCapacity}h) is less than weighted
              study load ({plan.totalWeightedHours}h). Consider more daily hours or an
              earlier start.
            </div>
          )}

          {/* Subject coverage cards */}
          <div className="stats-grid">
            {plan.stats.map((s, i) => (
              <div className="stat-card" key={i}
                style={{ borderColor: s.color?.border, background: s.color?.bg + "55" }}>
                <div className="stat-top">
                  <span className="stat-name">{s.name}</span>
                  <span className="stat-diff" style={{ color: s.color?.text }}>{s.difficulty}</span>
                </div>
                <div className="stat-bar-wrap">
                  <div className="stat-bar-fill" style={{ width: `${s.pct}%`, background: s.color?.text }} />
                </div>
                <div className="stat-footer">
                  <span className="stat-pct" style={{ color: s.color?.text }}>{s.pct}% covered</span>
                  <span className="stat-hrs">{s.scheduled}/{s.originalHours}h</span>
                </div>
              </div>
            ))}
          </div>

          {/* Day calendar */}
          <div className="calendar-label">📅 Full Plan for <strong>{plan.name}</strong></div>
          <div className="calendar">
            {plan.calendar.map(day => (
              <div key={day.day}
                className={`day-card day-${day.type} ${expanded[day.day] ? "expanded" : ""}`}
                onClick={() => toggle(day.day)}>

                <div className="day-header">
                  <div className="day-meta">
                    <span className="day-num">Day {day.day}</span>
                    <span className="day-date">{day.date}</span>
                  </div>
                  <div className="day-right">
                    {day.type === "study" && (
                      <span className="day-hours">{day.totalStudied}h · {day.sessions.length} subject{day.sessions.length !== 1 ? "s" : ""}</span>
                    )}
                    {day.type === "revision"      && <span className="day-tag revision-tag">📝 Revision</span>}
                    {day.type === "buffer"         && <span className="day-tag buffer-tag">🌿 Rest Day</span>}
                    {day.type === "early_revision" && <span className="day-tag early-tag">✅ Bonus Revision</span>}
                    <span className="day-toggle">{expanded[day.day] ? "▲" : "▼"}</span>
                  </div>
                </div>

                {expanded[day.day] && (
                  <div className="day-body">

                    {day.type === "study" && (
                      <>
                        {day.sessions.map((s, i) => (
                          <div className="session-row" key={i} style={{ borderLeftColor: s.color?.text }}>
                            <div className="session-info">
                              <span className="session-name">{s.subject}</span>
                              <span className="session-diff" style={{ color: s.color?.text }}>{s.difficulty}</span>
                            </div>
                            <div className="session-right">
                              <div className="session-mini-bar-wrap">
                                <div className="session-mini-bar"
                                  style={{ width: `${Math.round((s.hours / day.totalStudied) * 100)}%`, background: s.color?.text }} />
                              </div>
                              <span className="session-hrs">{s.hours}h</span>
                            </div>
                          </div>
                        ))}
                        <div className="day-total-row">
                          <span>Total study time</span>
                          <span>{day.totalStudied}h</span>
                        </div>
                      </>
                    )}

                    {day.type === "revision" && (
                      <div className="revision-body">
                        <p className="revision-note">Consolidate all subjects — no new material today:</p>
                        <div className="revision-chips">
                          {day.subjects.map((s, i) => <span className="chip" key={i}>{s}</span>)}
                        </div>
                        <div className="tip-box">
                          💡 Do practice papers and flashcards. Aim for ~{Math.round(day.hours / day.subjects.length * 10) / 10}h per subject.
                        </div>
                      </div>
                    )}

                    {day.type === "buffer" && (
                      <div className="buffer-body">
                        <p>🌿 Scheduled rest day. Options:</p>
                        <ul>
                          <li>Catch up on any subject running behind</li>
                          <li>Light review of notes</li>
                          <li>Rest — recovery improves retention</li>
                        </ul>
                      </div>
                    )}

                    {day.type === "early_revision" && (
                      <div className="revision-body">
                        <p className="revision-note">All subjects covered ahead of schedule!</p>
                        <div className="tip-box">🎯 Use this {day.hours}h for bonus revision on your weakest subject.</div>
                      </div>
                    )}

                  </div>
                )}
              </div>
            ))}
          </div>

        </section>
      )}
    </main>
  );
}
